import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions, 
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Refresh as RetryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// SAP Design System
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';
import { SAPIconButton, SAPComboBox, PageLayout, FilterBar } from '../components';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// API imports
import { fetchClients } from '../api/features/clientData/clientApi';
import { fetchJobDetails, fetchJobNames } from '../api/features/jobDetail/jobDetailApi';
import { fetchIntegrationFailures, fetchIntegrationFailuresCount } from '../api/features/integrationFailures/integrationApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { epochToDateTime } from '../api/utils';
import { api } from '../api/fetchWrapper';
import { useLocation } from 'react-router-dom';

// Types
interface ClientOption {
  label: string;
  value: number;
  code: string;
}

interface JobOption {
  label: string;
  value: string;
}

interface ErrorOption {
  label: string;
  value: string;
}

// Using the existing API types
import { IntegrationFailureListItem, IntegrationFailureDetail } from '../api/features/integrationFailures/integration.types';

// Constants
const ROWS_PER_PAGE = 10;

// SAP Styled Components
const SAPPageTitle = styled(Typography)(({ theme }) => ({
  color: SAP_COLORS.sapGray7,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
  marginBottom: SAP_SPACING.sm,
}));

const SAPPageSubtitle = styled(Typography)(({ theme }) => ({
  color: SAP_COLORS.sapGray5,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  marginBottom: SAP_SPACING.xl,
}));

const SAPFilterCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${SAP_COLORS.sapWhite} 0%, ${SAP_COLORS.sapGray1} 100%)`,
  borderRadius: '12px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
  marginBottom: SAP_SPACING.xl,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapWhite,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.sm,
}));

const SAPTableHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  color: SAP_COLORS.sapGray7,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
  borderBottom: `2px solid ${SAP_COLORS.sapGray3}`,
  fontSize: '0.875rem',
}));

const SAPTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: '4px',
    '& fieldset': {
      borderColor: SAP_COLORS.sapGray3,
    },
    '&:hover fieldset': {
      borderColor: SAP_COLORS.sapBlue,
    },
    '&.Mui-focused fieldset': {
      borderColor: SAP_COLORS.sapBlue,
      boxShadow: `0 0 0 2px ${SAP_COLORS.sapLightBlue}`,
    },
  },
  '& .MuiInputLabel-root': {
    color: SAP_COLORS.sapGray5,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  },
  '& .MuiInputBase-input': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  },
}));

const SAPStatusChip = styled(Chip)(({ status }: { status: string }) => {
  const getStatusColor = (status: string) => {
    if (status.toLowerCase().includes('success') || status.toLowerCase().includes('complete')) {
      return { bg: SAP_COLORS.sapLightGreen, text: SAP_COLORS.sapGreen };
    }
    if (status.toLowerCase().includes('error') || status.toLowerCase().includes('fail')) {
      return { bg: SAP_COLORS.sapLightRed, text: SAP_COLORS.sapRed };
    }
    return { bg: SAP_COLORS.sapLightOrange, text: SAP_COLORS.sapOrange };
  };
  const colors = getStatusColor(status);
  return {
    backgroundColor: colors.bg,
    color: colors.text,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    fontSize: '0.75rem',
  };
});

export default function IntegrationInstanceFailure() {
  const { clientCode } = useClientCode();
  const isAdmin = clientCode === 'admin';
  const location = useLocation();

  // Read URL params for jobRunId and errorCode
  const urlParams = new URLSearchParams(location.search);
  const urlJobRunId = urlParams.get('jobRunId');
  const urlErrorCode = urlParams.get('errorCode');

  // State
  const [failures, setFailures] = useState<IntegrationFailureListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  
  // Filter state
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobOption | null>(null); // Back to dropdown
  const [selectedError, setSelectedError] = useState<ErrorOption | null>(null);
  const [step, setStep] = useState('');
  const [stepSearchCriteria, setStepSearchCriteria] = useState('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [jobRunId, setJobRunId] = useState<number | null>(urlJobRunId ? parseInt(urlJobRunId, 10) : null);
  
  // Options state
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  const [errorOptions, setErrorOptions] = useState<ErrorOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingErrors, setLoadingErrors] = useState(false);

  // Modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [retryModalOpen, setRetryModalOpen] = useState(false);
  const [selectedFailure, setSelectedFailure] = useState<IntegrationFailureListItem | null>(null);
  const [retrying, setRetrying] = useState(false);
  
  // Snackbar state

  // Load client options on component mount (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    fetchClients({ clientCode, firstResult: -1, maxResult: 0 })
      .then((clients) => {
        setClientOptions(clients.map((c: any) => ({ label: c.name, value: c.id, code: c.code })));
      })
      .catch(() => setClientOptions([]));
  }, [clientCode, isAdmin]);

  // Load job options
  // - Admin: when a client is selected
  // - Client users: all jobs for their clientCode (no explicit client selector)
  useEffect(() => {
    if (isAdmin) {
      if (selectedClient && selectedClient.value) {
        fetchJobDetails({ clientCode, clientId: selectedClient.value, firstResult: -1, maxResult: 0 })
          .then((jobs) =>
            setJobOptions(
              jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))
            )
          )
          .catch(() => setJobOptions([]));
        setSelectedJob(null);
      } else {
        setJobOptions([]);
        setSelectedJob(null);
      }
      return;
    }

    // Client users: fetch jobs scoped by their clientCode
    fetchJobDetails({ clientCode, firstResult: -1, maxResult: 0 })
      .then((jobs) =>
        setJobOptions(
          jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))
        )
      )
      .catch(() => setJobOptions([]));
  }, [selectedClient, clientCode, isAdmin]);

  // API Functions
  const loadDropdownOptions = async () => {
    setLoadingOptions(true);
    try {
      
      
      // Load clients first
      const clients = await fetchClients({ clientCode, firstResult: -1, maxResult: 0 });

      

      // Process client options
      const processedClients = clients.map((client: any) => ({
        label: client.name || client.code || 'Unknown',
        value: client.id,
        code: client.code,
      }));
      setClientOptions(processedClients);

      // Load all jobs and errors (without client filter)
      await loadJobsForClient(); // Load all jobs
      await loadErrorsForClient(); // Load all errors

      
    } catch (error) {
      
      // Set empty arrays to prevent errors
      setClientOptions([]);
      setJobOptions([]);
      setErrorOptions([]);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Load jobs based on selected client
  const loadJobsForClient = async (clientId?: number) => {
    try {
      
      
      const params: any = { 
        clientCode, 
        firstResult: -1, 
        maxResult: 0
      };
      
      if (clientId) {
        params.clientId = clientId;
      }

      const jobs = await fetchJobDetails(params);

      
      // Process job options - jobs come as arrays [jobId, interval, jobName, ...]
      const uniqueJobs = jobs.reduce((acc: JobOption[], job: any) => {
        // job[0] = jobId, job[2] = jobName
        const jobId = job[0];
        const jobName = job[2] || 'Unknown';
        
        // Check if this job ID already exists
        const exists = acc.find(item => item.value === jobId);
        if (!exists && jobId) {
          acc.push({
            label: jobName,
            value: jobId, // Use jobId as value for API parameter
          });
        } else if (exists) {
          logger.log('Duplicate job found:', jobName);
        }
        return acc;
      }, []);
      
      
      
      setJobOptions(uniqueJobs);
    } catch (error) {
      logger.error('Error loading jobs for client:', error);
      setJobOptions([]);
    }
  };

  // Load errors based on selected client
  const loadErrorsForClient = async (clientId?: number) => {
    setLoadingErrors(true);
    try {
      const params: any = { 
        firstResult: -1, 
        maxResult: 0
      };
      
      if (clientId) {
        params.clientId = clientId;
      }

      // API endpoint: /admin/errorPatternMaster/
      // When client is not selected: /admin/errorPatternMaster/?firstResult=-1&maxResult=0
      // When client is selected: /admin/errorPatternMaster/?firstResult=-1&maxResult=0&clientId={id}
      const endpoint = `/admin/errorPatternMaster/`;
      
      const response = await api.get(endpoint, params);

      // Handle different response structures
      let errorArray: any[] = [];
      
      // If response is an array, use it directly (handles empty array [] case)
      if (Array.isArray(response)) {
        errorArray = response;
      } 
      // If response is an object, check for data property or use empty array
      else if (response && typeof response === 'object') {
        // Check if response has a data property that is an array
        const responseData = (response as any).data;
        if (Array.isArray(responseData)) {
          errorArray = responseData;
        }
        // If response is empty object or has no data, use empty array
        else if (Object.keys(response).length === 0 || !responseData) {
          errorArray = [];
        }
      }

      // Process error options
      const processedErrors = errorArray.map((error: any) => ({
        label: error.errorCode || error.code || error.name || 'Unknown',
        value: error.errorCode || error.code || error.id || '',
      }));
      
      setErrorOptions(processedErrors);
    } catch (error) {
      logger.error('Error loading errors for client:', error);
      setErrorOptions([]);
    } finally {
      setLoadingErrors(false);
    }
  };

  // Load error options when client is selected or deselected
  useEffect(() => {
    if (selectedClient && selectedClient.value) {
      loadErrorsForClient(selectedClient.value);
    } else {
      loadErrorsForClient(); // Load all errors when no client selected
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  // Set error code from URL params on mount
  useEffect(() => {
    if (urlErrorCode && errorOptions.length > 0) {
      const errorOption = errorOptions.find(opt => opt.value === urlErrorCode || opt.label === urlErrorCode);
      if (errorOption) {
        setSelectedError(errorOption);
      } else {
        // If error code not in options, create a temporary option
        setSelectedError({ label: urlErrorCode, value: urlErrorCode });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlErrorCode, errorOptions]);

  // Auto-load data when URL params are present
  useEffect(() => {
    if (urlJobRunId && urlErrorCode) {
      loadFailures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlJobRunId, urlErrorCode]);

  const loadFailures = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const firstResult = page * ROWS_PER_PAGE;
      
      // Base parameters - always include these
      const params: any = { 
        firstResult, 
        maxResult: ROWS_PER_PAGE,
        searched: true
      };

      // Count API parameters (for filtered requests)
      const countParams: any = {
        searched: true,
        jobSearch: '', // Always include empty jobSearch parameter
      };

      // Use URL params if present, otherwise use filter state
      const activeErrorCode = urlErrorCode || selectedError?.value;
      const activeJobRunId = jobRunId;

      // Only add optional parameters if they have values
      if (selectedClient?.value) {
        params.clientId = selectedClient.value;
        countParams.clientId = selectedClient.value;
      }
      if (selectedJob?.value) {
        params.jobDetailId = selectedJob.value;
        countParams.jobDetailId = selectedJob.value;
      }
      if (activeErrorCode) {
        params.errorCode = activeErrorCode;
        countParams.errorCode = activeErrorCode;
      }
      if (activeJobRunId) {
        params.jobRunId = activeJobRunId;
        countParams.jobRunId = activeJobRunId;
      }
      if (step.trim()) {
        params.stepId = step.trim();
        countParams.stepId = step.trim();
      }
      if (stepSearchCriteria.trim()) {
        params.nextRequestJson = stepSearchCriteria.trim();
        countParams.nextRequestJson = stepSearchCriteria.trim();
      }
      
      // Only add date parameters if dates are selected
      if (fromDate) {
        const fromDateStr = fromDate.toISOString().split('T')[0]; // YYYY-MM-DD format
        params.fromDate = fromDateStr;
        params.lastUpdateDateFrom = fromDateStr;
        countParams.fromDate = fromDateStr;
      }
      if (toDate) {
        const toDateStr = toDate.toISOString().split('T')[0]; // YYYY-MM-DD format
        params.toDate = toDateStr;
        params.lastUpdateDateTo = toDateStr;
        countParams.toDate = toDateStr;
      }

      // Check if any filters are applied
      const hasFilters = selectedClient?.value || selectedJob?.value || activeErrorCode || 
                         step.trim() || stepSearchCriteria.trim() || fromDate || toDate || activeJobRunId;

      // Use different count API based on whether filters are applied or jobRunId is present
      let totalCount: number;
      if (hasFilters || activeJobRunId) {
        // Use the filtered count API endpoint (IIFcount when jobRunId is present)
        const countEndpoint = `/admin/integrationInstanceFailureRest/IIFcount`;
        const countResponse = await api.get(countEndpoint, countParams);
        
        // Handle different response formats
        if (typeof countResponse === 'number') {
          totalCount = countResponse;
        } else if (countResponse && typeof countResponse === 'object' && 'count' in countResponse) {
          totalCount = (countResponse as any).count;
        } else if (typeof countResponse === 'string') {
          // Handle string responses like "1 8" - extract the count
          const parts = countResponse.trim().split(' ');
          totalCount = parts.length > 0 ? parseInt(parts[parts.length - 1], 10) : 0;
        } else {
          totalCount = 0;
        }
      } else {
        // Use the standard count API when no filters
        totalCount = await fetchIntegrationFailuresCount({ clientCode, ...countParams });
      }

      const failureData = await fetchIntegrationFailures({ clientCode, ...params });

      setFailures(failureData);
      setRowCount(typeof totalCount === 'number' && !isNaN(totalCount) ? totalCount : 0);
    } catch (error) {
      setError('Failed to load integration failures');
      setFailures([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Event Handlers
  const handleClientChange = (event: any, newClient: ClientOption | null) => {
    setSelectedClient(newClient);
  };

  const handleSearch = () => {
    setPage(0);
    loadFailures();
  };

  const handleReset = () => {
    setSelectedClient(null);
    setSelectedJob(null); // Clear job dropdown
    setSelectedError(null);
    setStep('');
    setStepSearchCriteria('');
    setFromDate(null);
    setToDate(null);
    setPage(0);
    setFailures([]);
    setRowCount(0);
    setError(null);
    setJobOptions([]); // Clear job options
    setErrorOptions([]); // Clear error options
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    // Pagination component gives 1-based page, convert to 0-based
    setPage(newPage - 1);
    if (failures.length > 0) {
      setTimeout(() => loadFailures(), 0);
    }
  };

  const handleViewDetails = (failure: IntegrationFailureListItem) => {
    setSelectedFailure(failure);
    setViewModalOpen(true);
  };

  const handleRetry = (failure: IntegrationFailureListItem) => {
    setSelectedFailure(failure);
    setRetryModalOpen(true);
  };

  const handleConfirmRetry = async () => {
    if (!selectedFailure) {
      return;
    }

    setRetrying(true);
    try {
      const iifId = selectedFailure.integrationId;
      const endpoint = `/admin/invoke/retry/${iifId}`;
      
      // If api.post succeeds, it means we got 200 OK (fetchWrapper throws on non-200)
      await api.post(endpoint, {});
      
      // Close modal
      setRetryModalOpen(false);
      setSelectedFailure(null);
      
      // Show user-friendly success message
      toast.success('Successful, please check step history for response');
      
      // Refresh the data to show updated retry count
      setTimeout(() => {
        loadFailures();
      }, 1000);
    } catch (error: any) {
      // Close modal
      setRetryModalOpen(false);
      
      // Always show user-friendly error message instead of technical details
      toast.error('Something went wrong, try again later or contact admin team');
      
      setSelectedFailure(null);
    } finally {
      setRetrying(false);
    }
  };

  const formatDateTime = (timestamp: number) => {
    return timestamp ? epochToDateTime(timestamp) : '-';
  };

  const formatJson = (obj: any) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return 'Invalid JSON';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageLayout
        title="Integration Instance Failures"
        description="Monitor and analyze integration failure patterns"
        titleSx={{ color: SAP_COLORS.sapBlue }}
      >
        <FilterBar loading={loading}>
          <Box sx={{ width: '100%' }}>
            {/* Row 1 - Entity Filters: Client (admin only), Job, Error */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Client Dropdown - admin only */}
                {isAdmin && (
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(33.333% - 11px)' } }}>
                    <Autocomplete
                      options={clientOptions}
                      getOptionLabel={(option) => option.label}
                      value={selectedClient}
                      onChange={handleClientChange}
                      renderInput={(params) => (
                        <TextField {...params} label="Client Name" />
                      )}
                    />
                  </Box>
                )}

                {/* Job Name Dropdown */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(33.333% - 11px)' } }}>
                  <Autocomplete
                    options={jobOptions}
                    getOptionLabel={(option) => option.label}
                    value={selectedJob}
                    onChange={(_, newValue) => setSelectedJob(newValue)}
                    disabled={isAdmin && !selectedClient}
                    renderInput={(params) => (
                      <TextField {...params} label="Job Name" />
                    )}
                  />
                </Box>

                {/* Error Name Dropdown */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(33.333% - 11px)' } }}>
                  <Autocomplete
                    options={errorOptions}
                    value={selectedError}
                    onChange={(_, newValue) => setSelectedError(newValue)}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={(option) => option.label}
                    loading={loadingErrors}
                    noOptionsText={
                      isAdmin && selectedClient 
                        ? `No error pattern found for ${selectedClient.label}` 
                        : "No error patterns found"
                    }
                    key={`error-${errorOptions.length}-${selectedClient?.value || 'none'}`}
                    renderInput={(params) => (
                      <TextField {...params} label="Error Name" />
                    )}
                  />
                </Box>
              </Box>
            </Box>

            {/* Row 2 - Step Filters: Step, Step Search Criteria */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Step Input */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 8px)' } }}>
                  <TextField
                    label="Step"
                    value={step}
                    onChange={(e) => setStep(e.target.value)}
                    placeholder="Enter step ID"
                    fullWidth
                  />
                </Box>

                {/* Step Search Criteria */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 8px)' } }}>
                  <TextField
                    label="Step Search Criteria"
                    value={stepSearchCriteria}
                    onChange={(e) => setStepSearchCriteria(e.target.value)}
                    placeholder="Enter step search criteria"
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>

            {/* Row 3 - Date Range: From Date, To Date */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* From Date */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 8px)' } }}>
                  <DateTimePicker
                    label="From Date"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    ampm={false}
                    views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                    format="dd/MM/yyyy HH:mm:ss"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Box>

                {/* To Date */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 8px)' } }}>
                  <DateTimePicker
                    label="To Date"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    ampm={false}
                    views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                    format="dd/MM/yyyy HH:mm:ss"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Row 4 - Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: SAP_SPACING.md,
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                alignItems: 'center',
                paddingTop: SAP_SPACING.md,
                borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
              }}
            >
              <SAPIconButton
                icon="search"
                onClick={handleSearch}
                variant="primary"
                loading={loading}
                sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
              />
              <SAPIconButton
                icon="reset"
                onClick={handleReset}
                variant="secondary"
                sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
              />
            </Box>
          </Box>
        </FilterBar>

        {/* SAP Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              marginBottom: SAP_SPACING.lg, 
              borderRadius: '8px',
              backgroundColor: SAP_COLORS.sapLightRed,
              color: SAP_COLORS.sapRed,
              border: `1px solid ${SAP_COLORS.sapRed}`,
            }}
          >
            {error}
          </Alert>
        )}

        {/* SAP Data Table */}
        <SAPDataCard>
          <CardContent sx={{ padding: 0 }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                padding: SAP_SPACING.xl 
              }}>
                <CircularProgress 
                  size={40} 
                  sx={{ color: SAP_COLORS.sapBlue }} 
                />
              </Box>
            ) : failures.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                padding: SAP_SPACING.xl 
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: SAP_COLORS.sapGray5,
                    fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                    marginBottom: SAP_SPACING.md,
                  }}
                >
                  No integration failures found
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: SAP_COLORS.sapGray4,
                    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                  }}
                >
                  Try adjusting your search criteria or click Search to load data
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    boxShadow: 'none', 
                    backgroundColor: SAP_COLORS.sapWhite 
                  }}
                >
                  <Table sx={{ backgroundColor: SAP_COLORS.sapWhite }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: SAP_COLORS.sapLightBlue }}>
                        <SAPTableHeader>S.No.</SAPTableHeader>
                        <SAPTableHeader>Step</SAPTableHeader>
                        <SAPTableHeader>Date</SAPTableHeader>
                        <SAPTableHeader>Error Response</SAPTableHeader>
                        <SAPTableHeader>Retry</SAPTableHeader>
                        <SAPTableHeader>View</SAPTableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ backgroundColor: SAP_COLORS.sapWhite }}>
                      {failures.map((failure, index) => (
                        <TableRow 
                          key={failure.integrationId} 
                          hover 
                          sx={{ 
                            backgroundColor: index % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                            '&:hover': { 
                              backgroundColor: SAP_COLORS.hover,
                              cursor: 'pointer',
                            },
                          }}
                        >
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          }}>
                            {page * ROWS_PER_PAGE + index + 1}
                          </TableCell>
                          <TableCell sx={{ 
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                color: SAP_COLORS.sapGray7,
                              }}
                            >
                              {failure.stepId}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ 
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{
                                color: SAP_COLORS.sapGray6,
                                fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                              }}
                            >
                              {formatDateTime(failure.date)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ 
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                            maxWidth: '40%',
                            wordBreak: 'break-word',
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: SAP_COLORS.sapGray6,
                                fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {failure.errorResponse || failure.errorName || 'No error response'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ 
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            <SAPIconButton
                              icon="reset"
                              onClick={() => handleRetry(failure)}
                              variant="secondary"
                            />
                          </TableCell>
                          <TableCell sx={{ 
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            <SAPIconButton
                              icon="view"
                              onClick={() => handleViewDetails(failure)}
                              variant="secondary"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {failures.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {/* Pagination Info */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + failures.length, rowCount)} of {rowCount} integration failures
                      </Typography>
                    </Box>
                    
                    {/* Pagination Controls */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Rows per page</InputLabel>
                        <Select
                          value={ROWS_PER_PAGE}
                          onChange={(event: SelectChangeEvent<number>) => {
                            // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                          }}
                          label="Rows per page"
                        >
                          <MenuItem value={5}>5</MenuItem>
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={25}>25</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <Pagination
                        count={Math.ceil(rowCount / ROWS_PER_PAGE)}
                        page={page + 1}
                        onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                          // newPage is 1-based; handler will convert / store appropriately
                          handlePageChange(event, newPage);
                        }}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </SAPDataCard>

                {/* View Details Modal */}
        <Dialog 
          open={viewModalOpen} 
          onClose={() => setViewModalOpen(false)} 
          maxWidth="lg" 
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              margin: { xs: SAP_SPACING.sm, sm: SAP_SPACING.md },
              maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
              width: { xs: 'calc(100% - 16px)', sm: '90%', md: '85%' }
            }
          }}
          PaperProps={{
            sx: {
              bgcolor: SAP_COLORS.sapWhite,
              boxShadow: SAP_SHADOWS.lg,
              borderRadius: '12px',
              minHeight: { xs: 'auto', sm: '500px' },
              maxHeight: { xs: '95vh', sm: '90vh' },
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
        <DialogTitle 
          sx={{ 
            bgcolor: SAP_COLORS.sapGray1, 
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`, 
            px: { xs: SAP_SPACING.md, sm: SAP_SPACING.lg },
            py: SAP_SPACING.md,
            position: 'sticky',
            top: 0,
            zIndex: 1
          }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold, 
                  color: SAP_COLORS.sapBlue,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }}
              >
                Integration Failure Details
              </Typography>
              <IconButton 
                onClick={() => setViewModalOpen(false)}
                sx={{ 
                  color: SAP_COLORS.sapGray6,
                  p: SAP_SPACING.sm,
                  '&:hover': { 
                    bgcolor: SAP_COLORS.hover,
                    color: SAP_COLORS.sapGray7
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent 
            sx={{ 
              p: { xs: SAP_SPACING.md, sm: SAP_SPACING.lg }, 
              bgcolor: SAP_COLORS.sapGray1,
              flex: 1,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '10px'
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: SAP_COLORS.sapGray1,
                borderRadius: '5px'
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: SAP_COLORS.sapGray3,
                borderRadius: '5px',
                '&:hover': {
                  bgcolor: SAP_COLORS.sapGray4
                }
              }
            }}
          >
            {selectedFailure && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: SAP_SPACING.lg,
                }}
              >
                {/* Basic Info Section */}
                <Box
                  sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: SAP_SPACING.md,
                    p: SAP_SPACING.lg,
                    bgcolor: SAP_COLORS.sapWhite,
                    borderRadius: 2,
                    border: `1px solid ${SAP_COLORS.sapGray2}`,
                    boxShadow: SAP_SHADOWS.sm,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: SAP_SPACING.xs }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: SAP_COLORS.sapGray5,
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Step ID
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: SAP_COLORS.sapGray7,
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                        fontSize: '1rem'
                      }}
                    >
                      {selectedFailure.stepId || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: SAP_SPACING.xs }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: SAP_COLORS.sapGray5,
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Date & Time
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: SAP_COLORS.sapGray7,
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                        fontSize: '1rem'
                      }}
                    >
                      {formatDateTime(selectedFailure.date)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: SAP_SPACING.xs }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: SAP_COLORS.sapGray5,
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Retry Count
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: SAP_COLORS.sapGray7,
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                        fontSize: '1rem'
                      }}
                    >
                      {selectedFailure.noOfRetries || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* Configuration Object Section */}
                <Box
                  sx={{
                    bgcolor: SAP_COLORS.sapWhite,
                    borderRadius: 2,
                    border: `1px solid ${SAP_COLORS.sapGray2}`,
                    boxShadow: SAP_SHADOWS.sm,
                    p: SAP_SPACING.lg,
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: SAP_COLORS.sapGray7,
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                      mb: SAP_SPACING.md,
                      fontSize: '1rem'
                    }}
                  >
                    Configuration Object
                  </Typography>
                  <Box sx={{ 
                    bgcolor: SAP_COLORS.sapGray1, 
                    p: SAP_SPACING.md, 
                    borderRadius: 1, 
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                    border: `1px solid ${SAP_COLORS.sapGray2}`,
                    maxHeight: '250px',
                    overflow: 'auto',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    color: SAP_COLORS.sapGray7
                  }}>
                    {selectedFailure.configObjectJson ? formatJson(selectedFailure.configObjectJson) : (
                      <Typography sx={{ color: SAP_COLORS.sapGray5, fontStyle: 'italic' }}>
                        No configuration data available
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Error Response Section */}
                <Box
                  sx={{
                    bgcolor: SAP_COLORS.sapWhite,
                    borderRadius: 2,
                    border: `1px solid ${SAP_COLORS.sapRed}33`,
                    boxShadow: SAP_SHADOWS.sm,
                    p: SAP_SPACING.lg,
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: SAP_COLORS.sapRed,
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                      mb: SAP_SPACING.md,
                      fontSize: '1rem'
                    }}
                  >
                    Error Response
                  </Typography>
                  <Box sx={{ 
                    bgcolor: `${SAP_COLORS.sapLightRed}40`, 
                    p: SAP_SPACING.md, 
                    borderRadius: 1, 
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                    border: `1px solid ${SAP_COLORS.sapRed}`,
                    maxHeight: '300px',
                    overflow: 'auto',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    color: SAP_COLORS.sapRed
                  }}>
                    {selectedFailure.errorResponse || selectedFailure.errorName || (
                      <Typography sx={{ color: SAP_COLORS.sapGray5, fontStyle: 'italic' }}>
                        No error response available
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Next Request Data Section */}
                <Box
                  sx={{
                    bgcolor: SAP_COLORS.sapWhite,
                    borderRadius: 2,
                    border: `1px solid ${SAP_COLORS.sapGreen}33`,
                    boxShadow: SAP_SHADOWS.sm,
                    p: SAP_SPACING.lg,
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: SAP_COLORS.sapGreen,
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                      mb: SAP_SPACING.md,
                      fontSize: '1rem'
                    }}
                  >
                    Next Request Data
                  </Typography>
                  <Box sx={{ 
                    bgcolor: `${SAP_COLORS.sapLightGreen}40`, 
                    p: SAP_SPACING.md, 
                    borderRadius: 1, 
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                    border: `1px solid ${SAP_COLORS.sapGreen}`,
                    maxHeight: '350px',
                    overflow: 'auto',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    color: SAP_COLORS.sapGreen
                  }}>
                    {selectedFailure.nextRequestJson ? formatJson(selectedFailure.nextRequestJson) : (
                      <Typography sx={{ color: SAP_COLORS.sapGray5, fontStyle: 'italic' }}>
                        No request data available
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Retry Confirmation Modal */}
        <Dialog 
          open={retryModalOpen} 
          onClose={() => !retrying && setRetryModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              margin: { xs: 2, sm: 4 },
              maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
              width: { xs: 'calc(100% - 32px)', sm: 'auto' }
            }
          }}
          PaperProps={{
            sx: {
              bgcolor: 'white',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              borderRadius: { xs: 2, sm: 3 },
              minHeight: { xs: 'auto', sm: '200px' },
              maxHeight: { xs: '90vh', sm: '60vh' },
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <DialogTitle 
            sx={{ 
              bgcolor: 'white', 
              borderBottom: '2px solid #e0e0e0', 
              pb: 2,
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#1a237e',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }}
              >
                Confirm Retry
              </Typography>
              <IconButton 
                onClick={() => setRetryModalOpen(false)}
                sx={{ 
                  color: '#1a237e',
                  p: { xs: 1, sm: 1.5 },
                  '&:hover': { 
                    bgcolor: '#e3f2fd',
                    color: '#0d47a1'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent 
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              bgcolor: 'white',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography 
              variant="body1" 
              color="#5a6270" 
              sx={{ 
                fontWeight: 500,
                textAlign: 'center',
                fontSize: { xs: '0.95rem', sm: '1rem' }
              }}
            >
              Are you sure you want to retry this integration?
            </Typography>
          </DialogContent>
          <DialogActions 
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              gap: { xs: 1, sm: 2 }, 
              bgcolor: 'white', 
              borderTop: '1px solid #e0e0e0',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center'
            }}
          >
            <Button 
              onClick={() => setRetryModalOpen(false)} 
              variant="outlined" 
              disabled={retrying}
              sx={{ 
                borderColor: '#e0e0e0', 
                color: '#5a6270',
                minWidth: { xs: '100%', sm: '120px' },
                '&:hover': {
                  borderColor: '#bdbdbd',
                  bgcolor: '#f5f5f5'
                },
                '&:disabled': {
                  borderColor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmRetry} 
              variant="contained" 
              disabled={retrying}
              startIcon={retrying ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
              sx={{ 
                bgcolor: '#1a237e', 
                minWidth: { xs: '100%', sm: '120px' },
                '&:hover': { bgcolor: '#0d47a1' },
                '&:disabled': {
                  bgcolor: '#9e9e9e',
                }
              }}
            >
              {retrying ? 'Retrying...' : 'Retry'}
            </Button>
          </DialogActions>
        </Dialog>
      </PageLayout>
    </LocalizationProvider>
  );
} 